import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'chunk_reload_timestamp';
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Detects if an error is caused by a failed dynamic import (stale chunk after deployment).
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('mime type')
  );
}

/**
 * Wrapper around React.lazy that auto-reloads the page on chunk load failures.
 * This handles the case where a new deployment replaces JS chunks while users
 * still have the old main bundle cached.
 *
 * Includes a cooldown to prevent infinite reload loops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComponentType<any> is required because React's ComponentType variance fails on memoized/forwardRef components exported via lazy imports.
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch((error: unknown) => {
      if (isChunkLoadError(error)) {
        const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
        const now = Date.now();

        if (now - lastReload > RELOAD_COOLDOWN_MS) {
          sessionStorage.setItem(RELOAD_KEY, String(now));
          window.location.reload();
          // Return a never-resolving promise to prevent rendering while reloading
          return new Promise<never>(() => {});
        }
      }
      // Re-throw if not a chunk error or if we already reloaded recently
      throw error;
    })
  );
}

export { lazyWithRetry, isChunkLoadError };
